import React, { useEffect, useState } from "react";
import axios from "axios";
import "../../styles/UserManagement.css";

const API_URL = "http://localhost:5133/api/HRManagerUsers";

const UserManagement = () => {
  const [users, setUsers] = useState([]);

  const [showForm, setShowForm] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    password: "",
    role: "Employee",
  });

  const [search, setSearch] = useState("");

  const [editingId, setEditingId] = useState(null);

  const [loading, setLoading] = useState(false);

  const [error, setError] = useState("");

  // Get users from backend
  const fetchUsers = async () => {
    try {
      setLoading(true);
      setError("");

      const response = await axios.get(API_URL);

      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);

      setError("Unable to load users.");
    } finally {
      setLoading(false);
    }
  };

  // Load users when page opens
  useEffect(() => {
    fetchUsers();
  }, []);

  // Handle input changes
  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  // Add or update user
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setError("");

      if (editingId) {
        // UPDATE
        await axios.put(
          `${API_URL}/${editingId}`,
          {
            name: formData.name,
            email: formData.email,
            role: formData.role,
          }
        );

        alert("User updated successfully.");
      } else {
        // CREATE
        await axios.post(API_URL, {
          name: formData.name,
          email: formData.email,
          password: formData.password,
          role: formData.role,
        });

        alert("User added successfully.");
      }

      // Reload users from database
      await fetchUsers();

      resetForm();

    } catch (error) {
      console.error("Error saving user:", error);

      if (error.response?.data) {
        setError(error.response.data);
      } else {
        setError("Something went wrong.");
      }
    }
  };

  // Edit user
  const handleEdit = (user) => {
    setEditingId(user.id);

    setFormData({
      name: user.name,
      email: user.email,
      password: "",
      role: user.role,
    });

    setShowForm(true);

    setError("");
  };

  // Delete user
  const handleDelete = async (id) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this user?"
    );

    if (!confirmDelete) {
      return;
    }

    try {
      setError("");

      await axios.delete(`${API_URL}/${id}`);

      alert("User deleted successfully.");

      await fetchUsers();

    } catch (error) {
      console.error("Error deleting user:", error);

      setError("Unable to delete user.");
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      email: "",
      password: "",
      role: "Employee",
    });

    setEditingId(null);
    setShowForm(false);
    setError("");
  };

  // Search users
  const filteredUsers = users.filter(
    (user) =>
      user.name?.toLowerCase().includes(search.toLowerCase()) ||
      user.email?.toLowerCase().includes(search.toLowerCase()) ||
      user.role?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="user-management-page">

      {/* Header */}
      <div className="user-management-header">
        <div>
          <h1>User Management</h1>
          <p>Manage Employee and HR user accounts</p>
        </div>

        <button
          className="add-user-btn"
          onClick={() => {
            resetForm();
            setShowForm(true);
          }}
        >
          + Add User
        </button>
      </div>

      {/* Error Message */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Search */}
      <div className="user-search-section">
        <input
          type="text"
          placeholder="Search users..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      {/* Add / Edit Form */}
      {showForm && (
        <div className="user-form-card">

          <div className="form-header">
            <h2>
              {editingId ? "Edit User" : "Add User"}
            </h2>

            <button
              className="close-btn"
              onClick={resetForm}
            >
              ×
            </button>
          </div>

          <form onSubmit={handleSubmit}>

            <div className="form-grid">

              {/* Name */}
              <div className="form-group">
                <label>Full Name</label>

                <input
                  type="text"
                  name="name"
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Email */}
              <div className="form-group">
                <label>Email</label>

                <input
                  type="email"
                  name="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleChange}
                  required
                />
              </div>

              {/* Password */}
              {!editingId && (
                <div className="form-group">
                  <label>Password</label>

                  <input
                    type="password"
                    name="password"
                    placeholder="Enter password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                  />
                </div>
              )}

              {/* Role */}
              <div className="form-group">
                <label>Role</label>

                <select
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                >
                  <option value="Employee">
                    Employee
                  </option>

                  <option value="HR">
                    HR
                  </option>
                </select>
              </div>

            </div>

            <div className="form-actions">

              <button
                type="button"
                className="cancel-btn"
                onClick={resetForm}
              >
                Cancel
              </button>

              <button
                type="submit"
                className="save-user-btn"
              >
                {editingId ? "Update User" : "Add User"}
              </button>

            </div>

          </form>
        </div>
      )}

      {/* Users Table */}
      <div className="users-table-card">

        <div className="table-header">
          <div>
            <h2>Users</h2>

            <span>
              {loading
                ? "Loading..."
                : `${filteredUsers.length} users found`}
            </span>
          </div>
        </div>

        <div className="table-container">

          <table>

            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>

              {filteredUsers.length > 0 ? (

                filteredUsers.map((user, index) => (

                  <tr key={user.id}>

                    <td>{index + 1}</td>

                    <td>
                      <div className="user-name">
                        {user.name}
                      </div>
                    </td>

                    <td>
                      {user.email}
                    </td>

                    <td>
                      <span
                        className={`role-badge ${
                          user.role === "HR"
                            ? "hr-role"
                            : "employee-role"
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>

                    <td>
                      <div className="action-buttons">

                        <button
                          className="edit-btn"
                          onClick={() => handleEdit(user)}
                        >
                          Edit
                        </button>

                        <button
                          className="delete-btn"
                          onClick={() => handleDelete(user.id)}
                        >
                          Delete
                        </button>

                      </div>
                    </td>

                  </tr>

                ))

              ) : (

                <tr>
                  <td
                    colSpan="5"
                    className="no-users"
                  >
                    {loading
                      ? "Loading users..."
                      : "No users found"}
                  </td>
                </tr>

              )}

            </tbody>

          </table>

        </div>
      </div>

    </div>
  );
};

export default UserManagement;